import {
  $createParagraphNode,
  $createTextNode,
  $getSelection,
  $isElementNode,
  $isRangeSelection,
  $isRootOrShadowRoot,
  type LexicalNode,
  type PointType,
  type TabNode,
} from 'lexical';
import { $dfs } from '@lexical/utils';
import { LinkNode, $isLinkNode } from '@lexical/link';
import { CodeNode, $isCodeHighlightNode } from '@lexical/code';
import { TableNode, $isTableRowNode, $isTableCellNode } from '@lexical/table';
import { $createHeadingNode, type HeadingNode } from '@lexical/rich-text';
import { $isListItemNode, type ListNode } from '@lexical/list';
import type { HorizontalRuleNode } from '@lexical/react/LexicalHorizontalRuleNode';

/** Moves every child of `node` to sit before it, then removes the now-empty
 *  node — the text survives, the wrapper does not. */
function $unwrapNode(node: LinkNode | TableNode | CodeNode): void {
  for (const child of node.getChildren()) {
    node.insertBefore(child);
  }
  node.remove();
}

/** The editor offers no link tool, so any link — pasted, dragged in, or in a
 *  restored draft — is dropped to plain text, keeping only its label. Runs as a
 *  node transform so every path that could introduce a link is covered. */
export function $stripLink(link: LinkNode): void {
  $unwrapNode(link);
}

/** Explicit whole-tree sweep for the read-only ScriptDocRenderer, which
 *  parses and displays a doc but never mounts DocHygienePlugin (it has no
 *  editing surface to protect). Run once right after `setEditorState`
 *  instead of registering a node transform there: a link/autolink reaching
 *  storage through any path other than this editor (a migration bug, a
 *  direct write, a future import) must never render live on this surface,
 *  which has no auth in front of it. */
export function $stripAllLinks(root: LexicalNode): void {
  for (const { node } of $dfs(root)) {
    if ($isLinkNode(node)) {
      $stripLink(node);
    }
  }
}

/** Collects the block children of every cell, in reading order. */
function $tableCellBlocks(table: TableNode): LexicalNode[] {
  const blocks: LexicalNode[] = [];
  for (const row of table.getChildren()) {
    if (!$isTableRowNode(row)) continue;
    for (const cell of row.getChildren()) {
      if (!$isTableCellNode(cell)) continue;
      blocks.push(...cell.getChildren());
    }
  }
  return blocks;
}

/** The toolbar offers no table tools, so a pasted table would be uneditable.
 *  Flatten it: each cell's blocks become top-level blocks where the table was. */
export function $flattenTable(table: TableNode): void {
  for (const block of $tableCellBlocks(table)) {
    table.insertBefore(block);
  }
  table.remove();
}

/** Same reasoning as tables: a pasted code block becomes an ordinary
 *  paragraph, keeping its text and line breaks but dropping highlight tokens. */
export function $codeBlockToParagraph(code: CodeNode): void {
  const paragraph = $createParagraphNode();
  for (const child of code.getChildren()) {
    const plain = $isCodeHighlightNode(child) ? $createTextNode(child.getTextContent()) : child;
    paragraph.append(plain);
  }
  code.replace(paragraph);
}

/** A Google Docs divider line pastes in as a horizontal rule, which the toolbar
 *  cannot make and the server rejects, so it becomes an empty paragraph. Docs
 *  can wrap the rule in a heading or paragraph; that wrapper is replaced too, so
 *  no block ends up nested inside another. */
export function $horizontalRuleToParagraph(rule: HorizontalRuleNode): void {
  const parent = rule.getParent();
  if ($isElementNode(parent) && !$isRootOrShadowRoot(parent)) {
    if (parent.getChildrenSize() === 1) {
      parent.replace($createParagraphNode());
    } else {
      rule.remove();
    }
    return;
  }
  rule.replace($createParagraphNode());
}

const TAB_AS_SPACES = '    ';

/** The server rejects tab nodes, which a pasted tab or the Tab key mid-line
 *  creates, so each becomes spaces with the same formatting. replace() keeps a
 *  caret's offset into the tab, which would leave it one space in, so a caret
 *  after the tab is moved to the end of the spaces. */
export function $tabToSpaces(tab: TabNode): void {
  const spaces = $createTextNode(TAB_AS_SPACES);
  spaces.setFormat(tab.getFormat());
  spaces.setStyle(tab.getStyle());
  const before = $getSelection();
  const isAfterTab = (point: PointType) => point.key === tab.getKey() && point.offset > 0;
  const moveAnchor = $isRangeSelection(before) && isAfterTab(before.anchor);
  const moveFocus = $isRangeSelection(before) && isAfterTab(before.focus);
  tab.replace(spaces);
  // replace() swaps in a new selection object, so read it again.
  const after = $getSelection();
  if (!$isRangeSelection(after)) return;
  if (moveAnchor) after.anchor.set(spaces.getKey(), TAB_AS_SPACES.length, 'text');
  if (moveFocus) after.focus.set(spaces.getKey(), TAB_AS_SPACES.length, 'text');
}

const TOOLBAR_HEADING_TAGS: ReadonlySet<string> = new Set(['h1', 'h2', 'h3']);

/** The toolbar and the server stop at H3, but pasted Docs, Notion and web
 *  headings can be H4–H6, so those become H3. */
export function $headingToToolbarLevel(heading: HeadingNode): void {
  if (TOOLBAR_HEADING_TAGS.has(heading.getTag())) return;
  const h3 = $createHeadingNode('h3');
  h3.setFormat(heading.getFormatType());
  h3.setIndent(heading.getIndent());
  heading.replace(h3, true);
}

/** A pasted Google Docs checklist arrives as a check list, which the toolbar
 *  cannot make and the server rejects, so it becomes a bulleted list. */
export function $checklistToBullets(list: ListNode): void {
  if (list.getListType() !== 'check') return;
  list.setListType('bullet');
  for (const item of list.getChildren()) {
    if ($isListItemNode(item)) item.setChecked(undefined);
  }
}
